import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Globe, BookOpen, Users, Calendar, Star, Award, Image as ImageIcon, ArrowRight, ExternalLink } from "lucide-react"
import { Button } from "../../components/ui/button"
import { Helmet } from "react-helmet-async"
import FeaturedPosts from "../../components/Blog/FeaturedPosts"
import { fetchPublicPosts } from "../../api/publicApi"
import coordinatorPhoto from "../../assets/AmericanCorner/Fulgence.jpg"

const americanCornerContext = require.context("../../assets/AmericanCorner", false, /\.(jpg|jpeg|png|webp)$/i)
const acKeys = americanCornerContext.keys()
const getAcSrc = (s) => (typeof s === "string" ? s : s?.default ?? s)
const AC_IMAGES = acKeys.slice(0, 10).map((key) => americanCornerContext(key))
const AC_HERO_SRC = AC_IMAGES.length > 0 ? getAcSrc(AC_IMAGES[0]) : null

export default function AmericanCorner() {
    const [featuredPosts, setFeaturedPosts] = useState([])

    useEffect(() => {
        fetchPublicPosts({ category: "blogOfSpaceOfStudents", limit: 3 }).then((data) => {
            setFeaturedPosts(data.posts || [])
        }).catch(() => { })
    }, [])

    const services = [
        {
            icon: BookOpen,
            title: "Conseil Éducatif",
            description: "Orientation et accompagnement pour les étudiants souhaitant poursuivre leurs études supérieures aux États-Unis.",
            color: "from-primary/10 to-accent/10"
        },
        {
            icon: Users,
            title: "Ateliers Professionnels",
            description: "Formations interactives sur le développement de carrière, l'entrepreneuriat et le leadership.",
            color: "from-accent/10 to-primary/10"
        },
        {
            icon: Globe,
            title: "Apprentissage de l'Anglais",
            description: "Clubs et ressources dédiés pour aider les étudiants et professionnels à améliorer leur maîtrise de l'anglais.",
            color: "from-primary/10 to-accent/10"
        },
        {
            icon: Calendar,
            title: "Clubs Numériques et Tech",
            description: "Initiation aux nouvelles technologies et développement des compétences numériques essentielles du 21e siècle.",
            color: "from-accent/10 to-primary/10"
        },
        {
            icon: ImageIcon,
            title: "Conférences & Films",
            description: "Discussions et projections sur les affaires internationales, le changement social et les réalisations historiques.",
            color: "from-primary/10 to-accent/10"
        },
        {
            icon: Award,
            title: "Avantages Exclusifs (Gratuit)",
            description: "Internet haut débit, accès eLibraryUSA, catalogue en ligne et autres ressources en libre accès.",
            color: "from-accent/10 to-primary/10"
        }
    ]

    const navSections = [
        { id: "presentation", label: "Présentation" },
        { id: "services", label: "Les services" },
        { id: "responsable", label: "Coordonnateur" },
    ]

    return (
        <div className="min-h-screen bg-background font-sans text-foreground antialiased">
            <Helmet>
                <title>American Corner - Université de Ngozi</title>
                <meta
                    name="description"
                    content="L'American Corner Ngozi : un espace dédié à la promotion de la culture américaine et à l'apprentissage de l'anglais."
                />
            </Helmet>

            {/* Hero Section */}
            <header className="relative min-h-[60vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    {AC_HERO_SRC && (
                        <img
                            src={AC_HERO_SRC}
                            alt="American Corner Ngozi"
                            className="w-full h-full object-cover opacity-40 mix-blend-luminosity scale-105"
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Espaces Étudiants</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">Culture & Anglais</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">American Corner Ngozi</span>
                            <span className="block text-white mt-2">Votre Fenêtre sur les États-Unis</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-6 mb-10 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-sm md:text-base font-light leading-relaxed text-primary-foreground/90">
                                Un espace dédié à la promotion de la culture américaine et à l'apprentissage de l'anglais.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <a href="#presentation">
                                <Button className="bg-accent text-accent-foreground px-10 py-7 text-sm font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl">
                                    Découvrir
                                    <ArrowRight className="w-5 h-5 ml-3" />
                                </Button>
                            </a>
                        </div>
                    </div>
                </div>
                <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce opacity-20">
                    <div className="w-px h-12 bg-white" />
                </div>
            </header>

            <article className="container py-12 lg:py-16 max-w-5xl mx-auto">
                <main className="space-y-16">
                    {/* Breadcrumb */}
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground -mt-4 mb-2">
                        <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
                        <span>/</span>
                        <Link to="/etudiants/blog" className="hover:text-primary transition-colors">Étudiants</Link>
                        <span>/</span>
                        <span className="text-foreground font-medium">American Corner Ngozi</span>
                    </div>

                    {/* Compact summary */}
                    <nav className="flex flex-wrap items-center gap-2 py-3 border-y border-border/80">
                        <span className="text-[10px] uppercase tracking-widest font-black text-muted-foreground mr-1">Sommaire</span>
                        {navSections.map((item) => (
                            <a
                                key={item.id}
                                href={`#${item.id}`}
                                className="px-2.5 py-1 rounded-md text-[11px] font-bold text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors"
                            >
                                {item.label}
                            </a>
                        ))}
                    </nav>

                    {/* Présentation */}
                    <section id="presentation" className="scroll-mt-24">
                        <h2 className="text-xl lg:text-2xl font-serif font-black italic text-foreground mb-5 flex items-center gap-2">
                            <div className="w-8 h-0.5 bg-accent rounded-full" />
                            Présentation de l'espace (Mission et Histoire)
                        </h2>
                        <div className="grid lg:grid-cols-2 gap-8 items-start">
                            <div className="relative group order-2 lg:order-1">
                                <div className="aspect-[4/3] bg-gradient-to-br from-primary/20 via-accent/10 to-primary/20 rounded-xl overflow-hidden border border-border flex items-center justify-center relative">
                                    {AC_IMAGES[1] ? (
                                        <img src={getAcSrc(AC_IMAGES[1])} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    ) : (
                                        <>
                                            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5" />
                                            <div className="relative text-center p-4">
                                                <ImageIcon className="w-12 h-12 text-primary/40 mx-auto mb-2" />
                                                <p className="text-xs font-medium text-muted-foreground">American Corner</p>
                                                <p className="text-[10px] text-muted-foreground">Espace Étudiant · UNG</p>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-4 order-1 lg:order-2 text-muted-foreground leading-relaxed text-sm">
                                <p>
                                    L'American Corner Ngozi est un service de recherche et d'information de la Section de Diplomatie Publique de l'Ambassade des États-Unis. Établi d'abord comme un "American Shelf" en 2021, il a obtenu le statut complet d'American Corner en mai 2022 avant d'être officiellement inauguré le 19 décembre 2022.
                                </p>
                                <p>
                                    Hébergé à l'Université de Ngozi, cet environnement accueillant fait partie d'un réseau mondial de plus de 620 espaces américains dans 150 pays.
                                </p>

                                <div className="mt-6 pt-6 border-t border-border">
                                    <h3 className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                                        <Star className="w-4 h-4 text-accent" />
                                        Nos Objectifs
                                    </h3>
                                    <ul className="space-y-2 list-disc list-inside">
                                        <li><strong className="text-foreground">Information :</strong> Ressources sur les États-Unis.</li>
                                        <li><strong className="text-foreground">Éducation :</strong> Soutien aux étudiants et enseignants.</li>
                                        <li><strong className="text-foreground">Compétences :</strong> Développement numérique et professionnel.</li>
                                        <li><strong className="text-foreground">Culture :</strong> Dialogue sur le leadership et l'entrepreneuriat.</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Services */}
                    <section id="services" className="scroll-mt-24">
                        <h2 className="text-xl lg:text-2xl font-serif font-black italic text-foreground mb-3 flex items-center gap-2">
                            <div className="w-8 h-0.5 bg-accent rounded-full" />
                            Les services de l'espace
                        </h2>
                        <p className="text-sm text-muted-foreground mb-5 max-w-2xl">L'American Corner offre un large éventail de services et programmes :</p>
                        <div className="grid md:grid-cols-2 gap-4">
                            {services.map((service, idx) => {
                                const Icon = service.icon
                                return (
                                    <div key={idx} className="group p-5 bg-secondary/5 rounded-xl border border-border hover:border-accent/30 transition-all duration-300">
                                        <Icon className="w-9 h-9 text-primary mb-4 opacity-30 group-hover:opacity-100 group-hover:text-accent transition-all" />
                                        <h3 className="text-sm font-black uppercase tracking-tight text-foreground mb-1.5">{service.title}</h3>
                                        <p className="text-xs text-muted-foreground leading-relaxed">{service.description}</p>
                                    </div>
                                )
                            })}
                        </div>
                    </section>

                    {/* Responsable */}
                    <section id="responsable" className="scroll-mt-24">
                        <h2 className="text-xl lg:text-2xl font-serif font-black italic text-foreground mb-5 flex items-center gap-2">
                            <div className="w-8 h-0.5 bg-accent rounded-full" />
                            Biographie du coordonnateur
                        </h2>
                        <div className="flex flex-col sm:flex-row gap-5 p-5 sm:p-6 rounded-xl border border-border bg-secondary/5 hover:border-primary/20 transition-colors">
                            <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-xl overflow-hidden border border-border flex-shrink-0 bg-primary/5 mt-3">
                                <img src={typeof coordinatorPhoto === "string" ? coordinatorPhoto : coordinatorPhoto?.default} alt="TWIZERIMANA Fulgence" className="w-full h-full object-cover" />
                            </div>
                            <div className="space-y-3 flex-1 text-sm">
                                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">Coordonnateur</p>
                                <h3 className="text-lg font-serif font-black italic text-foreground">TWIZERIMANA Fulgence</h3>
                                <p className="text-muted-foreground leading-relaxed">
                                    Fulgence Twizerimana dirige l'American Corner Ngozi depuis son évolution d'un "American Shelf" en 2021 jusqu'à son inauguration officielle en 2022. Son vaste parcours dans le leadership civique et la consolidation de la paix correspond parfaitement à la mission de l'espace, ce qui lui permet de servir de pont essentiel pour la compréhension mutuelle entre les États-Unis et la population burundaise.
                                </p>
                                <p className="text-muted-foreground leading-relaxed">
                                    En tant qu'Assistant en Anglais à la Faculté des Lettres et co-fondateur du <em>Peace English Club</em>, il utilise les langues et les arts comme catalyseurs de changement, tout en dotant les jeunes des compétences essentielles du 21e siècle (numérique, entrepreneuriat, leadership).
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Gallery */}
                    <section>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl lg:text-2xl font-serif font-black italic text-foreground flex items-center gap-2">
                                <div className="w-8 h-0.5 bg-accent rounded-full" />
                                Nos espaces
                            </h2>
                            <Link to="/galerie" className="text-[11px] font-bold text-primary hover:text-accent transition-colors flex items-center gap-1 uppercase tracking-widest">
                                Voir la galerie
                                <ExternalLink className="w-3 h-3" />
                            </Link>
                        </div>
                        {AC_IMAGES.length >= 4 ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[AC_IMAGES[2], AC_IMAGES[6], AC_IMAGES[7], AC_IMAGES[5]].filter(Boolean).map((src, idx) => (
                                    <div key={idx} className="group aspect-square bg-secondary/5 rounded-xl border border-border hover:border-accent/30 transition-all cursor-pointer overflow-hidden">
                                        <img src={getAcSrc(src)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </section>

                    {/* Actualités & Blog */}
                    <section aria-labelledby="actualites-blog-heading">
                        <h2 id="actualites-blog-heading" className="text-xl lg:text-2xl font-serif font-black italic text-foreground mb-3 flex items-center gap-2">
                            <div className="w-8 h-0.5 bg-accent rounded-full" />
                            Actualités & Blog
                        </h2>
                        <p className="text-sm text-muted-foreground max-w-2xl mb-5 leading-relaxed">
                            Découvrez les dernières actualités, événements et ressources de l'American Corner. Restez informé sur nos programmes, nos ateliers et les opportunités d'échange culturel et académique.
                        </p>
                        <FeaturedPosts
                            posts={featuredPosts}
                            space="american-corner"
                            limit={3}
                            showViewAll={true}
                            blogPath="/etudiants/blog/american-corner"
                            showSectionTitle={false}
                            emptyMessage="Aucun article publié pour le moment. Revenez bientôt."
                        />
                    </section>
                </main>
            </article>

            {/* Footer CTA */}
            <footer id="rejoindre" className="bg-primary text-white border-t-8 border-accent relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/5 -skew-x-12 origin-top-right pointer-events-none" />
                <div className="container py-16 text-center relative z-10">
                    <div className="max-w-2xl mx-auto space-y-10">
                        <div className="inline-flex items-center gap-6 mb-4">
                            <div className="h-px w-8 bg-accent" />
                            <span className="text-[11px] uppercase tracking-[0.4em] text-accent font-black">Comment Devenir Membre ?</span>
                            <div className="h-px w-8 bg-accent" />
                        </div>
                        <h2 className="text-2xl lg:text-4xl font-serif italic mb-6 leading-tight">
                            L'adhésion à l'American Corner est<br />
                            <span className="text-accent underline decoration-accent/30 decoration-4 underline-offset-4">totalement gratuite</span>.
                        </h2>
                        <p className="text-primary-foreground/90 max-w-xl mx-auto leading-relaxed text-sm md:text-base">
                            Il vous suffit de remplir un formulaire d'adhésion pour obtenir un accès complet à l'espace et à toutes ses ressources, incluant les clubs, l'Internet haut débit et la bibliothèque en ligne.
                        </p>
                        <p className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-medium mt-8">
                            © {new Date().getFullYear()} Université de Ngozi · American Corner
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    )
}
